import {
  useGetMyStatusQuery,
  useGetMyTradesQuery,
  useGetMyFeesQuery,
  useGetMyPendingQuery,
  useGetMyPositionsQuery,
  useGetMySettingsQuery,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Alert } from "@/components/ui/alert";
import { Table, THead, TBody, TR, TH, TD } from "@/components/ui/table";
import { EquityCurve } from "@/components/charts/EquityCurve";
import { DailyPnL } from "@/components/charts/DailyPnL";
import { fmtUsd, fmtTime, fmtRelativeFuture } from "@/lib/format";
import { Link } from "react-router-dom";
import { DcaHoldings } from "@/components/domain/DcaHoldings";

export default function UserOverview() {
  const status = useGetMyStatusQuery(undefined, { pollingInterval: 15000 });
  const trades = useGetMyTradesQuery({ limit: 25 }, { pollingInterval: 30000 });
  const fees = useGetMyFeesQuery();
  const pending = useGetMyPendingQuery({ status: "pending", limit: 50 }, { pollingInterval: 5000 });
  const closed = useGetMyPositionsQuery(
    { status: "closed", limit: 5000 },
    { pollingInterval: 60000 },
  );

  const s = status.data;
  const settings = s?.settings;
  // Per-user engine flags — hide cards for engines this user doesn't run
  // (e.g. hard-stopped accounts) so no obsolete DCA/cycle figures show.
  const eng = (useGetMySettingsQuery().data as any) || {};
  const dcaOn = !!eng.dca_enabled;
  const cyclesOn = !!(eng.per_user_cycle_enabled || eng.can_run_cycle);
  const tradeRows = (trades.data ?? []) as unknown as Array<{
    id: number; executed_at: string; action: string; symbol: string;
    size_usd: number; filled_qty: number; avg_price: number; dry_run: number;
  }>;

  return (
    <div className="space-y-4">
      {/* DCA cost-basis summary — only when DCA is enabled for this user. */}
      {dcaOn && <DcaHoldings pollMs={30000} />}

      {/* Cycles (hourly) charts — only when the hourly cycle is enabled for
          this user (hidden for hard-stopped accounts to avoid stale figures). */}
      {cyclesOn && (
      <div className="grid lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>
              Cycles — equity curve
              <span className="text-xs font-normal text-muted ml-2">
                rolling closed hourly P&L
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {closed.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              <EquityCurve positions={(closed.data ?? []) as any} height={180} />
            )}
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>
              Cycles — daily P&L
              <span className="text-xs font-normal text-muted ml-2">last 14 days</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {closed.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : (
              <DailyPnL positions={(closed.data ?? []) as any} days={14} height={180} />
            )}
          </CardContent>
        </Card>
      </div>
      )}

      {/* Next scheduled cycle — read from /api/me/status. */}
      {s?.next_cycle_at && (
        <p className="text-xs text-muted">
          Next cycle runs at{" "}
          <span className="text-fg tabular">
            {fmtTime(s.next_cycle_at)}
          </span>
          {" "}({fmtRelativeFuture(s.next_cycle_at)}).
        </p>
      )}

      {!s?.has_keys && (
        <Alert tone="warn" title="Add your Binance keys">
          The bot will not trade on your account until you save Binance API
          keys + per-order limits in{" "}
          <Link to="/settings" className="text-accent underline">Settings</Link>.
        </Alert>
      )}

      {/* KPI row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi label="Free USDT" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">
            {s?.free_quote_usdt != null ? fmtUsd(s.free_quote_usdt) : "—"}
          </span>
          <div className="text-xs text-muted">on your Binance</div>
        </Kpi>
        <Kpi label="Per-order cap" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">
            {settings?.per_order_usd != null ? fmtUsd(Number(settings.per_order_usd)) : "—"}
          </span>
          <div className="text-xs text-muted">your setting</div>
        </Kpi>
        <Kpi label="Trades today" loading={status.isLoading}>
          <span className="tabular text-2xl font-semibold">
            {s?.trades_today ?? 0}
            {settings?.max_orders_per_day != null && (
              <span className="text-muted text-sm"> / {Number(settings.max_orders_per_day)}</span>
            )}
          </span>
          <div className="text-xs text-muted">cap resets at midnight UTC</div>
        </Kpi>
        <Kpi label="Profit-share owed" loading={fees.isLoading}>
          <span className="tabular text-2xl font-semibold">
            {fees.data ? fmtUsd(fees.data.summary.outstanding_fee) : "—"}
          </span>
          <div className="text-xs text-muted">10% of this month's net profit</div>
        </Kpi>
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <Card>
          <CardHeader>
            <CardTitle>
              Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge tone={settings?.paused ? "warn" : "good"}>
                {settings?.paused ? "paused" : "active"}
              </Badge>
              <Badge tone={s?.has_keys ? "good" : "warn"}>
                {s?.has_keys ? "API keys on file" : "no keys"}
              </Badge>
              <Badge tone="info">
                {String(settings?.approval_mode ?? "manual")}
              </Badge>
            </div>
            <div className="text-xs text-muted">
              {(pending.data?.length ?? 0) > 0 ? (
                <>
                  You have {pending.data?.length} trade(s) waiting for approval —{" "}
                  <Link to="/pending" className="text-accent underline">review now</Link>
                </>
              ) : (
                <>No trades waiting for your approval right now.</>
              )}
            </div>
            <div className="pt-2">
              <Link to="/settings" className="text-sm text-accent underline">
                Edit limits & keys →
              </Link>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>10% profit-share — monthly net</CardTitle>
          </CardHeader>
          <CardContent>
            {fees.isLoading || !fees.data ? (
              <Skeleton className="h-16 w-full" />
            ) : (
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-xs text-muted uppercase">Outstanding</div>
                  <div className="text-lg font-semibold">
                    {fmtUsd(fees.data.summary.outstanding_fee)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted uppercase">Paid</div>
                  <div className="text-lg font-semibold">{fmtUsd(fees.data.summary.paid_fee)}</div>
                </div>
                <div>
                  <div className="text-xs text-muted uppercase">Months</div>
                  <div className="text-lg font-semibold">
                    {fees.data.summary.n_periods ?? fees.data.summary.n_accruals ?? 0}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent trades on your account</CardTitle>
        </CardHeader>
        <CardContent>
          {trades.isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : tradeRows.length === 0 ? (
            <p className="text-sm text-muted">No trades yet — once the bot fan-outs to your
              account or you approve a pending trade, it'll show up here.</p>
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>When</TH>
                  <TH>Action</TH>
                  <TH>Symbol</TH>
                  <TH className="text-right">Size</TH>
                  <TH className="text-right">Price</TH>
                  <TH>Mode</TH>
                </TR>
              </THead>
              <TBody>
                {tradeRows.map((t) => (
                  <TR key={t.id}>
                    <TD className="text-xs text-muted">{fmtTime(t.executed_at)}</TD>
                    <TD>
                      <Badge tone={t.action === "BUY" ? "good" : "bad"}>{t.action}</Badge>
                    </TD>
                    <TD className="font-medium">{t.symbol}</TD>
                    <TD className="text-right tabular">{fmtUsd(t.size_usd)}</TD>
                    <TD className="text-right tabular">
                      {t.avg_price ? Number(t.avg_price).toFixed(4) : "—"}
                    </TD>
                    <TD>
                      <Badge tone={t.dry_run ? "warn" : "muted"}>
                        {t.dry_run ? "dry-run" : "live"}
                      </Badge>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Kpi({ label, loading, children }: {
  label: string; loading?: boolean; children: React.ReactNode;
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="text-xs text-muted uppercase tracking-wide mb-1">{label}</div>
        {loading ? <Skeleton className="h-7 w-24" /> : children}
      </CardContent>
    </Card>
  );
}
